import { z } from 'zod';
import { IsoDateTimeSchema } from './common.js';
export const PlaylistManifestItemSchema = z.object({
    assetId: z.string().min(1),
    url: z.string().url(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    durationSeconds: z.number().positive(),
    mimeType: z.string().min(1),
});
/** Immutable playlist document delivered to devices. */
export const PlaylistManifestSchema = z.object({
    screenId: z.string().min(1),
    version: z.number().int().positive(),
    generatedAt: IsoDateTimeSchema,
    items: z.array(PlaylistManifestItemSchema).min(1),
});
// ---------------------------------------------------------------------------
// Admin CRUD (dashboard-facing). The manifest above is the immutable,
// device-facing document produced by publishing; these schemas govern the
// mutable draft that precedes it.
// ---------------------------------------------------------------------------
export const PlaylistKindSchema = z.enum(['standard', 'direct']);
export const CreatePlaylistRequestSchema = z.object({
    name: z.string().min(1).max(200),
});
export const UpdatePlaylistRequestSchema = z.object({
    name: z.string().min(1).max(200),
});
export const PlaylistSchema = z.object({
    id: z.string().min(1),
    organisationId: z.string().min(1),
    name: z.string().min(1),
    /** `direct` = screen-owned single-item show; omitted from the Playlists library. */
    kind: PlaylistKindSchema,
    /** Set only for `direct` playlists — the screen this quick show belongs to. */
    sourceScreenId: z.string().min(1).nullable(),
    createdAt: IsoDateTimeSchema,
    /** Last rename, draft edit (add / remove / reorder / duration), publish, or rollback. */
    updatedAt: IsoDateTimeSchema,
});
export const AddPlaylistItemRequestSchema = z.object({
    mediaAssetId: z.string().min(1),
    durationSeconds: z.number().positive(),
});
export const UpdatePlaylistItemRequestSchema = z.object({
    durationSeconds: z.number().positive().max(300),
});
/** Full draft order after a drag-reorder (permutation of existing item ids). */
export const ReorderPlaylistItemsRequestSchema = z.object({
    itemIds: z.array(z.string().min(1)).min(1),
});
export const PlaylistItemSchema = z.object({
    id: z.string().min(1),
    playlistId: z.string().min(1),
    position: z.number().int().nonnegative(),
    mediaAssetId: z.string().min(1),
    durationSeconds: z.number().positive(),
});
export const PlaylistPublisherSchema = z.object({
    id: z.string().min(1),
    email: z.string().email(),
    displayName: z.string().nullable(),
});
export const PlaylistVersionSummarySchema = z.object({
    version: z.number().int().positive(),
    publishedAt: IsoDateTimeSchema,
    publishedBy: PlaylistPublisherSchema.nullable(),
    previousVersion: z.number().int().positive().nullable(),
    itemCount: z.number().int().nonnegative(),
});
/** Live vs draft status for the publish toolbar. */
export const PlaylistPublishStateSchema = z.object({
    playlistId: z.string().min(1),
    live: PlaylistVersionSummarySchema.nullable(),
    draftItemCount: z.number().int().nonnegative(),
    hasUnpublishedChanges: z.boolean(),
});
export const PlaylistVersionDetailSchema = PlaylistVersionSummarySchema.extend({
    items: z.array(PlaylistManifestItemSchema),
});
export const RollbackPlaylistRequestSchema = z.object({
    toVersion: z.number().int().positive(),
});
export const PublishPlaylistResponseSchema = z.object({
    playlistId: z.string().min(1),
    version: z.number().int().positive(),
    publishedAt: IsoDateTimeSchema,
    publishedBy: PlaylistPublisherSchema.nullable(),
    previousVersion: z.number().int().positive().nullable(),
});
//# sourceMappingURL=playlist.js.map